import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Target, Settings, TrendingUp, TrendingDown, 
  Calendar, RefreshCcw, Save, Eye, Edit,
  CheckCircle, Clock, Loader2, Award, Users, UserCheck, Layers, History, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const TargetDefinitionPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [staffBrokerageConfigs, setStaffBrokerageConfigs] = useState([]);
  const [brokerageAuditLog, setBrokerageAuditLog] = useState([]);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showBrokerageConfigDialog, setShowBrokerageConfigDialog] = useState(false);
  const [showAuditLogDialog, setShowAuditLogDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [configEdit, setConfigEdit] = useState({});
  const [brokerageConfigEdit, setBrokerageConfigEdit] = useState({
    user_id: '',
    target_method: 'salary_multiplier',
    salary: '',
    multiplier: 4,
    aum_percentage: '',
    reason: ''
  });
  const [generating, setGenerating] = useState(false);

  const isPrincipal = user?.role === 'principal' || user?.role === 'business_head';

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/target-definition/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setConfigEdit(data);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, [token]);

  // Fetch all target definitions
  const fetchDefinitions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/target-definition/definitions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDefinitions(data);
      }
    } catch (err) {
      console.error('Error fetching definitions:', err);
    }
  }, [token]);

  // Fetch staff list
  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStaffList(data.filter(s => s.role === 'rm' || s.role === 'dealer'));
      }
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  }, [token]);

  // Fetch staff brokerage configurations
  const fetchStaffBrokerageConfigs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/target-definition/staff-brokerage-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStaffBrokerageConfigs(data);
      }
    } catch (err) {
      console.error('Error fetching staff brokerage configs:', err);
    }
  }, [token]);

  // Fetch brokerage config audit log
  const fetchBrokerageAuditLog = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/target-definition/brokerage-config-audit-log?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBrokerageAuditLog(data);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchConfig(), 
        fetchDefinitions(), 
        fetchStaff(),
        isPrincipal ? fetchStaffBrokerageConfigs() : Promise.resolve(),
        isPrincipal ? fetchBrokerageAuditLog() : Promise.resolve()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchConfig, fetchDefinitions, fetchStaff, fetchStaffBrokerageConfigs, fetchBrokerageAuditLog, isPrincipal]);

  // Update configuration
  const handleSaveConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/target-definition/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(configEdit),
      });
      if (res.ok) {
        toast.success('Configuration saved. Regenerating all targets...');
        
        // Auto-regenerate all targets after saving config
        try {
          const regenRes = await fetch(`${API_URL}/target-definition/generate-all`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (regenRes.ok) {
            toast.success('All targets regenerated successfully');
          } else {
            toast.warning('Config saved but failed to regenerate targets');
          }
        } catch (regenErr) {
          console.error('Error regenerating targets:', regenErr);
        }
        
        setShowConfigDialog(false);
        fetchConfig();
        fetchDefinitions();
      } else {
        toast.error('Failed to save configuration');
      }
    } catch (err) {
      toast.error('Error saving configuration');
    }
  };

  // Get staff brokerage config
  const getStaffBrokerageConfig = (userId) => {
    return staffBrokerageConfigs.find(c => c.user_id === userId);
  };

  // Open edit brokerage config dialog
  const handleEditBrokerageConfig = (staff) => {
    const existingConfig = getStaffBrokerageConfig(staff.id);
    setBrokerageConfigEdit({
      user_id: staff.id,
      target_method: existingConfig?.target_method || 'salary_multiplier',
      salary: existingConfig?.salary || '',
      multiplier: existingConfig?.multiplier || 4,
      aum_percentage: existingConfig?.aum_percentage || '',
      reason: ''
    });
    setSelectedStaff(staff);
    setShowBrokerageConfigDialog(true);
  };

  // Save staff brokerage config
  const handleSaveBrokerageConfig = async () => {
    try {
      const payload = {
        user_id: brokerageConfigEdit.user_id,
        target_method: brokerageConfigEdit.target_method,
        reason: brokerageConfigEdit.reason || 'Configuration updated'
      };

      if (brokerageConfigEdit.target_method === 'salary_multiplier') {
        if (!brokerageConfigEdit.salary || brokerageConfigEdit.salary <= 0) {
          toast.error('Please enter a valid salary amount');
          return;
        }
        if (!brokerageConfigEdit.multiplier || brokerageConfigEdit.multiplier <= 0) {
          toast.error('Please enter a valid multiplier');
          return;
        }
        payload.salary = parseFloat(brokerageConfigEdit.salary);
        payload.multiplier = parseFloat(brokerageConfigEdit.multiplier);
      } else {
        if (!brokerageConfigEdit.aum_percentage || brokerageConfigEdit.aum_percentage <= 0) {
          toast.error('Please enter a valid AUM percentage');
          return;
        }
        payload.aum_percentage = parseFloat(brokerageConfigEdit.aum_percentage);
      }

      const res = await fetch(`${API_URL}/target-definition/staff-brokerage-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success('Brokerage target configuration saved. Targets regenerated.');
        setShowBrokerageConfigDialog(false);
        fetchStaffBrokerageConfigs();
        fetchBrokerageAuditLog();
        fetchDefinitions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save configuration');
      }
    } catch (err) {
      toast.error('Error saving brokerage configuration');
    }
  };

  // Generate targets for a user
  const handleGenerateForUser = async (userId) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/target-definition/generate/${userId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Targets generated successfully');
        fetchDefinitions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate targets');
      }
    } catch (err) {
      toast.error('Error generating targets');
    } finally {
      setGenerating(false);
    }
  };

  // Generate for all users
  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/target-definition/generate-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Generated targets for ${data.rms.length} RMs and ${data.dealers.length} Dealers`);
        fetchDefinitions();
      } else {
        toast.error('Failed to generate targets');
      }
    } catch (err) {
      toast.error('Error generating targets');
    } finally {
      setGenerating(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  // Get definition for user
  const getDefinition = (userId, periodType) => {
    return definitions.find(d => d.user_id === userId && d.period_type === periodType);
  };

  // View staff details
  const handleViewDetails = (staff) => {
    setSelectedStaff(staff);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="target-definition-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Target Definition</h1>
          <p className="text-slate-400">Configure and assign targets for RMs and Dealers</p>
        </div>
        <div className="flex gap-2">
          {isPrincipal && (
            <>
              <Button
                variant="ghost"
                onClick={() => setShowAuditLogDialog(true)}
                className="text-slate-400 hover:text-white"
              >
                <History className="w-4 h-4 mr-2" />
                Change Log
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowConfigDialog(true)}
                className="border-[#C5A059]/30"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configure Defaults
              </Button>
              <Button
                onClick={handleGenerateAll}
                disabled={generating}
                className="bg-[#C5A059] hover:bg-[#b4914e]"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCcw className="w-4 h-4 mr-2" />
                )}
                Generate All Targets
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Current Configuration Summary */}
      {config && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Daily Targets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Traded Clients</span>
                  <span className="text-white">{config.daily?.traded_clients?.active_percentage || config.daily?.traded_clients?.default_percentage || 10}% of active</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Brokerage</span>
                  <span className="text-white">{config.daily?.brokerage?.default_percentage || 0.0045}% of AUM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Client Activation</span>
                  <span className="text-white">{config.daily?.client_activation?.default_value || 2} from Dormant</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Tier Trade Targets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Platinum</span>
                  <span className="text-cyan-400">{config.daily?.tier_trade?.platinum || 30}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Gold</span>
                  <span className="text-yellow-400">{config.daily?.tier_trade?.gold || 30}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Silver</span>
                  <span className="text-gray-300">{config.daily?.tier_trade?.silver || 30}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Blue</span>
                  <span className="text-blue-400">{config.daily?.tier_trade?.blue || 10}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Weekly Targets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Brokerage</span>
                  <span className="text-white">{config.weekly?.brokerage?.multiplier || 4.75}× Daily</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Monthly Targets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Brokerage</span>
                  <span className="text-white">{config.monthly?.brokerage?.default_percentage || 0.09}% of AUM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">New Clients</span>
                  <span className="text-white">{config.monthly?.new_clients?.default_value || 5}/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Meetings</span>
                  <span className="text-white">{config.monthly?.meetings?.default_value || 5}/month</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Staff Targets Table */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#C5A059]">All Staff</TabsTrigger>
          <TabsTrigger value="rms" className="data-[state=active]:bg-[#C5A059]">RMs</TabsTrigger>
          <TabsTrigger value="dealers" className="data-[state=active]:bg-[#C5A059]">Dealers</TabsTrigger>
        </TabsList>

        {['all', 'rms', 'dealers'].map((tab) => (
          <TabsContent key={tab} value={tab}>
            <Card className="card-dark border-white/5">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-400 sticky left-0 bg-slate-900">Name</TableHead>
                      <TableHead className="text-slate-400">Role</TableHead>
                      <TableHead className="text-slate-400">Brokerage Config</TableHead>
                      <TableHead className="text-slate-400 text-center" colSpan={2}>Daily</TableHead>
                      <TableHead className="text-slate-400 text-center">Weekly</TableHead>
                      <TableHead className="text-slate-400 text-center" colSpan={3}>Monthly</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-right text-slate-400">Actions</TableHead>
                    </TableRow>
                    <TableRow className="border-white/10">
                      <TableHead className="sticky left-0 bg-slate-900"></TableHead>
                      <TableHead></TableHead>
                      <TableHead className="text-slate-500 text-xs">Method</TableHead>
                      <TableHead className="text-slate-500 text-xs">Traded</TableHead>
                      <TableHead className="text-slate-500 text-xs">Brokerage</TableHead>
                      <TableHead className="text-slate-500 text-xs">Brokerage</TableHead>
                      <TableHead className="text-slate-500 text-xs">Brokerage</TableHead>
                      <TableHead className="text-slate-500 text-xs">New Clients</TableHead>
                      <TableHead className="text-slate-500 text-xs">Meetings</TableHead>
                      <TableHead></TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffList
                      .filter(s => tab === 'all' || (tab === 'rms' && s.role === 'rm') || (tab === 'dealers' && s.role === 'dealer'))
                      .map((member) => {
                        const dailyDef = getDefinition(member.id, 'daily');
                        const weeklyDef = getDefinition(member.id, 'weekly');
                        const monthlyDef = getDefinition(member.id, 'monthly');
                        const hasTargets = dailyDef || weeklyDef || monthlyDef;
                        const brokerageConfig = getStaffBrokerageConfig(member.id);

                        return (
                          <TableRow key={member.id} className="border-white/5 hover:bg-white/5">
                            <TableCell className="text-white font-medium sticky left-0 bg-slate-900">{member.name}</TableCell>
                            <TableCell>
                              <Badge className={member.role === 'rm' ? 'bg-blue-600' : 'bg-purple-600'}>
                                {member.role.toUpperCase()}
                              </Badge>
                            </TableCell>
                            {/* Brokerage Config - View Only (edit in Configure Defaults) */}
                            <TableCell>
                              {brokerageConfig ? (
                                <Badge className={brokerageConfig.target_method === 'salary_multiplier' ? 'bg-green-600' : 'bg-blue-600'}>
                                  {brokerageConfig.target_method === 'salary_multiplier' ? (
                                    <span className="text-xs">₹{(brokerageConfig.salary || 0).toLocaleString('en-IN')} × {brokerageConfig.multiplier}</span>
                                  ) : (
                                    <span className="text-xs">{brokerageConfig.aum_percentage}% AUM</span>
                                  )}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-slate-500 text-slate-400 text-xs">
                                  Using Default
                                </Badge>
                              )}
                              ) : (
                            </TableCell>
                            {/* Daily Traded Clients */}
                            <TableCell className="text-white text-center">
                              {dailyDef?.targets?.traded_clients?.target ?? '-'}
                            </TableCell>
                            {/* Daily Brokerage */}
                            <TableCell className="text-white text-center">
                              {dailyDef ? formatCurrency(dailyDef.targets?.brokerage?.target) : '-'}
                            </TableCell>
                            {/* Weekly Brokerage */}
                            <TableCell className="text-white text-center">
                              {weeklyDef ? formatCurrency(weeklyDef.targets?.brokerage?.target) : '-'}
                            </TableCell>
                            {/* Monthly Brokerage */}
                            <TableCell className="text-white text-center">
                              {monthlyDef ? formatCurrency(monthlyDef.targets?.brokerage?.target) : '-'}
                            </TableCell>
                            {/* Monthly New Clients */}
                            <TableCell className="text-white text-center">
                              {monthlyDef?.targets?.new_clients?.target ?? '-'}
                            </TableCell>
                            {/* Monthly Meetings */}
                            <TableCell className="text-white text-center">
                              {monthlyDef?.targets?.meetings?.target ?? '-'}
                            </TableCell>
                            <TableCell>
                              {hasTargets ? (
                                <Badge className="bg-emerald-600">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Set
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-amber-500 text-amber-500">
                                  <Clock className="w-3 h-3 mr-1" /> Pending
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewDetails(member)}
                                className="hover:bg-white/10"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isPrincipal && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleGenerateForUser(member.id)}
                                  disabled={generating}
                                  className="border-[#C5A059]/30 hover:bg-[#C5A059]/10"
                                >
                                  {generating ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <RefreshCcw className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {staffList.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-8 text-slate-400">
                          No staff members found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Staff Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C5A059]" />
              {selectedStaff?.name} - Target Details
            </DialogTitle>
            <DialogDescription>Complete breakdown of targets by period</DialogDescription>
          </DialogHeader>

          {selectedStaff && (
            <div className="space-y-6">
              {/* Daily Targets */}
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Daily Targets
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(() => {
                    const def = getDefinition(selectedStaff.id, 'daily');
                    return (
                      <>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Traded Clients</p>
                          <p className="text-white text-lg font-bold">{def?.targets?.traded_clients?.target ?? 0}</p>
                          <p className="text-slate-500 text-xs">
                            {def?.targets?.traded_clients?.active_percentage || def?.targets?.traded_clients?.percentage || 10}% active
                            {def?.targets?.traded_clients?.from_d2 > 0 && ` + ${def?.targets?.traded_clients?.from_d2} D2`}
                            {def?.targets?.traded_clients?.from_d3 > 0 && ` + ${def?.targets?.traded_clients?.from_d3} D3`}
                          </p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Brokerage</p>
                          <p className="text-white text-lg font-bold">{formatCurrency(def?.targets?.brokerage?.target)}</p>
                          <p className="text-slate-500 text-xs">{def?.targets?.brokerage?.percentage ?? 0.0045}% of AUM</p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Client Activation</p>
                          <p className="text-white text-lg font-bold">{def?.targets?.client_activation?.target ?? 2}</p>
                          <p className="text-slate-500 text-xs">From Dormant pool</p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Tier Trade</p>
                          <div className="flex gap-2 mt-1">
                            <span className="text-cyan-400 text-xs">P:{def?.targets?.tier_trade?.platinum ?? 0}</span>
                            <span className="text-yellow-400 text-xs">G:{def?.targets?.tier_trade?.gold ?? 0}</span>
                            <span className="text-gray-300 text-xs">S:{def?.targets?.tier_trade?.silver ?? 0}</span>
                            <span className="text-blue-400 text-xs">B:{def?.targets?.tier_trade?.blue ?? 0}</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Weekly Targets */}
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Weekly Targets
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {(() => {
                    const def = getDefinition(selectedStaff.id, 'weekly');
                    return (
                      <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">Brokerage</p>
                        <p className="text-white text-lg font-bold">{formatCurrency(def?.targets?.brokerage?.target)}</p>
                        <p className="text-slate-500 text-xs">{def?.targets?.brokerage?.multiplier ?? 4.75}× Daily</p>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Monthly Targets */}
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Monthly Targets
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {(() => {
                    const def = getDefinition(selectedStaff.id, 'monthly');
                    return (
                      <>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Brokerage</p>
                          <p className="text-white text-lg font-bold">{formatCurrency(def?.targets?.brokerage?.target)}</p>
                          <p className="text-slate-500 text-xs">{def?.targets?.brokerage?.percentage ?? 0.09}% of AUM</p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">New Clients</p>
                          <p className="text-white text-lg font-bold">{def?.targets?.new_clients?.target ?? 5}</p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Meetings</p>
                          <p className="text-white text-lg font-bold">{def?.targets?.meetings?.target ?? 5}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Base Metrics */}
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Base Metrics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(() => {
                    const def = getDefinition(selectedStaff.id, 'daily');
                    return (
                      <>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Total AUM</p>
                          <p className="text-[#C5A059] text-lg font-bold">{formatCurrency(def?.targets?.brokerage?.base_aum)}</p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Active Clients</p>
                          <p className="text-white text-lg font-bold">{def?.targets?.traded_clients?.base_value ?? 0}</p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3">
                          <p className="text-slate-400 text-xs">Dormant Pool</p>
                          <p className="text-white text-lg font-bold">{def?.targets?.client_activation?.dormant_pool ?? 0}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Target Configuration</DialogTitle>
            <DialogDescription>Configure default target values for all users</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Daily Config */}
            <div className="space-y-4">
              <h3 className="text-white font-medium border-b border-white/10 pb-2">Daily Targets</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Traded Clients - Active (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configEdit.daily?.traded_clients?.active_percentage || 10}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      daily: {
                        ...configEdit.daily,
                        traded_clients: { ...configEdit.daily?.traded_clients, active_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Active clients × this %</p>
                </div>
                <div>
                  <Label>Brokerage (% of AUM)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={configEdit.daily?.brokerage?.default_percentage || 0.0045}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      daily: {
                        ...configEdit.daily,
                        brokerage: { ...configEdit.daily?.brokerage, default_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Default: 0.0045%</p>
                </div>
                <div>
                  <Label>Client Activation (count)</Label>
                  <Input
                    type="number"
                    value={configEdit.daily?.client_activation?.default_value || 2}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      daily: {
                        ...configEdit.daily,
                        client_activation: { ...configEdit.daily?.client_activation, default_value: parseInt(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Dormant 3 clients to activate</p>
                </div>
              </div>
            </div>

            {/* Tier Trade Config */}
            <div className="space-y-4">
              <h3 className="text-white font-medium border-b border-white/10 pb-2">Tier Trade Targets (%)</h3>
              <div className="grid grid-cols-4 gap-4">
                {['platinum', 'gold', 'silver', 'blue'].map((tier) => (
                  <div key={tier}>
                    <Label className="capitalize">{tier}</Label>
                    <Input
                      type="number"
                      value={configEdit.daily?.tier_trade?.[tier] || (tier === 'blue' ? 10 : 30)}
                      onChange={(e) => setConfigEdit({
                        ...configEdit,
                        daily: {
                          ...configEdit.daily,
                          tier_trade: { ...configEdit.daily?.tier_trade, [tier]: parseFloat(e.target.value) }
                        }
                      })}
                      className="bg-slate-800 border-white/10"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Weekly Config */}
            <div className="space-y-4">
              <h3 className="text-white font-medium border-b border-white/10 pb-2">Weekly Targets</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Traded - Active (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configEdit.weekly?.traded_clients?.active_percentage || 15}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      weekly: {
                        ...configEdit.weekly,
                        traded_clients: { ...configEdit.weekly?.traded_clients, active_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Active clients × this %</p>
                </div>
                <div>
                  <Label>Traded - Dormant 2 (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configEdit.weekly?.traded_clients?.dormant2_percentage || 5}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      weekly: {
                        ...configEdit.weekly,
                        traded_clients: { ...configEdit.weekly?.traded_clients, dormant2_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Dormant 2 clients × this %</p>
                </div>
                <div>
                  <Label>Brokerage Multiplier</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={configEdit.weekly?.brokerage?.multiplier || 4.75}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      weekly: {
                        ...configEdit.weekly,
                        brokerage: { ...configEdit.weekly?.brokerage, multiplier: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">× Daily Brokerage Target</p>
                </div>
              </div>
            </div>

            {/* Monthly Config */}
            <div className="space-y-4">
              <h3 className="text-white font-medium border-b border-white/10 pb-2">Monthly Targets</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Traded - Active (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configEdit.monthly?.traded_clients?.active_percentage || 20}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      monthly: {
                        ...configEdit.monthly,
                        traded_clients: { ...configEdit.monthly?.traded_clients, active_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Active clients × this %</p>
                </div>
                <div>
                  <Label>Traded - Dormant 2 (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configEdit.monthly?.traded_clients?.dormant2_percentage || 15}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      monthly: {
                        ...configEdit.monthly,
                        traded_clients: { ...configEdit.monthly?.traded_clients, dormant2_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Dormant 2 clients × this %</p>
                </div>
                <div>
                  <Label>Traded - Dormant 3 (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configEdit.monthly?.traded_clients?.dormant3_percentage || 5}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      monthly: {
                        ...configEdit.monthly,
                        traded_clients: { ...configEdit.monthly?.traded_clients, dormant3_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Dormant 3 clients × this %</p>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Brokerage (% of AUM)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={configEdit.monthly?.brokerage?.default_percentage || 0.09}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      monthly: {
                        ...configEdit.monthly,
                        brokerage: { ...configEdit.monthly?.brokerage, default_percentage: parseFloat(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Default: 0.09%</p>
                </div>
                <div>
                  <Label>New Clients</Label>
                  <Input
                    type="number"
                    value={configEdit.monthly?.new_clients?.default_value || 5}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      monthly: {
                        ...configEdit.monthly,
                        new_clients: { ...configEdit.monthly?.new_clients, default_value: parseInt(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                </div>
                <div>
                  <Label>Meetings</Label>
                  <Input
                    type="number"
                    value={configEdit.monthly?.meetings?.default_value || 5}
                    onChange={(e) => setConfigEdit({
                      ...configEdit,
                      monthly: {
                        ...configEdit.monthly,
                        meetings: { ...configEdit.monthly?.meetings, default_value: parseInt(e.target.value) }
                      }
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                </div>
              </div>
            </div>

            {/* Staff Brokerage Configuration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-2">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#C5A059]" />
                  Staff Brokerage Target Configuration
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAuditLogDialog(true)}
                  className="text-slate-400 hover:text-white text-xs"
                >
                  <History className="w-3 h-3 mr-1" /> View Change Log
                </Button>
              </div>
              <p className="text-xs text-slate-400">
                Configure brokerage targets per staff member using either Salary × Multiplier or % of AUM method.
              </p>
              
              <div className="max-h-60 overflow-y-auto space-y-2">
                {staffList.map((staff) => {
                  const brokerageConfig = getStaffBrokerageConfig(staff.id);
                  return (
                    <div 
                      key={staff.id}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <Badge className={staff.role === 'rm' ? 'bg-blue-600' : 'bg-purple-600'}>
                          {staff.role.toUpperCase()}
                        </Badge>
                        <span className="text-white">{staff.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {brokerageConfig ? (
                          <Badge className={brokerageConfig.target_method === 'salary_multiplier' ? 'bg-green-600' : 'bg-blue-600'}>
                            {brokerageConfig.target_method === 'salary_multiplier' ? (
                              <span className="text-xs">₹{(brokerageConfig.salary || 0).toLocaleString('en-IN')} × {brokerageConfig.multiplier}</span>
                            ) : (
                              <span className="text-xs">{brokerageConfig.aum_percentage}% AUM</span>
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-slate-500 text-slate-400 text-xs">
                            Using Default
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditBrokerageConfig(staff)}
                          className="h-7 px-2 border-[#C5A059]/30 hover:bg-[#C5A059]/10"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Configure
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} className="bg-[#C5A059] hover:bg-[#b4914e]">
              <Save className="w-4 h-4 mr-2" /> Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Brokerage Config Dialog */}
      <Dialog open={showBrokerageConfigDialog} onOpenChange={setShowBrokerageConfigDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#C5A059]" />
              Brokerage Target - {selectedStaff?.name}
            </DialogTitle>
            <DialogDescription>
              Set monthly brokerage target calculation method
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Method Selection */}
            <div className="space-y-2">
              <Label>Target Calculation Method</Label>
              <Select
                value={brokerageConfigEdit.target_method}
                onValueChange={(value) => setBrokerageConfigEdit({
                  ...brokerageConfigEdit,
                  target_method: value
                })}
              >
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="salary_multiplier">Salary × Multiplier</SelectItem>
                  <SelectItem value="aum_percentage">% of AUM</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Salary Multiplier Method Fields */}
            {brokerageConfigEdit.target_method === 'salary_multiplier' && (
              <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <DollarSign className="w-4 h-4" />
                  Salary × Multiplier Method
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Salary (₹)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 50000"
                      value={brokerageConfigEdit.salary}
                      onChange={(e) => setBrokerageConfigEdit({
                        ...brokerageConfigEdit,
                        salary: e.target.value
                      })}
                      className="bg-slate-800 border-white/10"
                    />
                  </div>
                  <div>
                    <Label>X Times (Multiplier)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      placeholder="e.g., 4"
                      value={brokerageConfigEdit.multiplier}
                      onChange={(e) => setBrokerageConfigEdit({
                        ...brokerageConfigEdit,
                        multiplier: e.target.value
                      })}
                      className="bg-slate-800 border-white/10"
                    />
                  </div>
                </div>
                {brokerageConfigEdit.salary && brokerageConfigEdit.multiplier && (
                  <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                    <p className="text-sm text-slate-400">Monthly Target Preview:</p>
                    <p className="text-xl font-bold text-green-400">
                      ₹{(parseFloat(brokerageConfigEdit.salary || 0) * parseFloat(brokerageConfigEdit.multiplier || 0)).toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      ₹{parseFloat(brokerageConfigEdit.salary || 0).toLocaleString('en-IN')} × {brokerageConfigEdit.multiplier}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* AUM Percentage Method Fields */}
            {brokerageConfigEdit.target_method === 'aum_percentage' && (
              <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-2 text-blue-400 text-sm">
                  <TrendingUp className="w-4 h-4" />
                  % of AUM Method
                </div>
                <div>
                  <Label>AUM Percentage (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 0.09"
                    value={brokerageConfigEdit.aum_percentage}
                    onChange={(e) => setBrokerageConfigEdit({
                      ...brokerageConfigEdit,
                      aum_percentage: e.target.value
                    })}
                    className="bg-slate-800 border-white/10"
                  />
                  <p className="text-xs text-slate-500 mt-1">Default is 0.09% of AUM</p>
                </div>
              </div>
            )}

            {/* Reason for Change */}
            <div>
              <Label>Reason for Change (Optional)</Label>
              <Input
                placeholder="e.g., Annual revision"
                value={brokerageConfigEdit.reason}
                onChange={(e) => setBrokerageConfigEdit({
                  ...brokerageConfigEdit,
                  reason: e.target.value
                })}
                className="bg-slate-800 border-white/10"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBrokerageConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveBrokerageConfig} className="bg-[#C5A059] hover:bg-[#b4914e]">
              <Save className="w-4 h-4 mr-2" /> Save & Regenerate Targets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brokerage Config Audit Log Dialog */}
      <Dialog open={showAuditLogDialog} onOpenChange={setShowAuditLogDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-[#C5A059]" />
              Brokerage Config Change History
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {brokerageAuditLog.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No changes recorded yet</p>
            ) : (
              brokerageAuditLog.map((log, idx) => (
                <div key={idx} className="p-4 bg-slate-800/50 rounded-lg border border-white/5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-white font-medium">{log.user_name}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(log.changed_at).toLocaleString()}
                      </p>
                    </div>
                    {log.reason && (
                      <Badge variant="outline" className="text-xs">{log.reason}</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-2 bg-red-500/10 rounded">
                      <p className="text-red-400 text-xs mb-1">Before</p>
                      {log.old_value ? (
                        <p className="text-slate-300">
                          {log.old_value.target_method === 'salary_multiplier' 
                            ? `₹${(log.old_value.salary || 0).toLocaleString()} × ${log.old_value.multiplier}`
                            : `${log.old_value.aum_percentage}% of AUM`
                          }
                        </p>
                      ) : (
                        <p className="text-slate-500">No previous config</p>
                      )}
                    </div>
                    <div className="p-2 bg-green-500/10 rounded">
                      <p className="text-green-400 text-xs mb-1">After</p>
                      <p className="text-slate-300">
                        {log.new_value.target_method === 'salary_multiplier'
                          ? `₹${(log.new_value.salary || 0).toLocaleString()} × ${log.new_value.multiplier}`
                          : `${log.new_value.aum_percentage}% of AUM`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAuditLogDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TargetDefinitionPage;
